import {
	Component,
	HostListener,
	Input,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
	SimpleChanges,
	ChangeDetectionStrategy,
	AfterViewInit,
} from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';

import { MediaObserver } from '@angular/flex-layout';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import {
	CarrierFeedback,
	CarrierTable,
	ContactsTable,
	DIFFERENCE_IN_DAYS,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	IS_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';
import { UrlService } from '@service/url/url.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import {
	ACCOUNT_TYPE,
	CARGOCHIEF_KEYS,
	CARRIER_FEEDBACK_SETTINGS,
	INDEXED_DB,
	PLAN_NAMES,
	S3_CSV_TEMPLATE,
	URLS,
} from '@domain/constants/app-variables';
import { TEMPLATE_TYPE } from '@domain/constants/email-templates';
import { UtilService } from '@service/util/util.service';
import { SharedService } from '@service/shared/shared.service';
import { IndexedDBService } from '@service/indexedDB/indexed-db.service';
import { SegmentEventsConstants } from '@domain/constants/segment-events-constants';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { IntercomService } from '@service/intercom/intercom.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { LaneDetailsComponent } from '../../carrier-full-profile/lane-details/lane-details.component';
import { SearchPipe } from '@shared/search.pipe';
import { AccountSettingsService } from '@service/accountSettings/account-settings.service';
import { environment } from '@environment/environment';
import { QuickSearchComponent } from '../quick-search/quick-search.component';
import { HeaderService } from '@service/header/header.service';
import { distinctUntilChanged, take, takeUntil, filter, finalize } from 'rxjs/operators';
import { ProfilePopoverComponent } from '../profile-popover/profile-popover.component';
import { Subscription } from 'rxjs';
import { ProjectHelperService } from '@service/projectHelper/project-helper.service';
import { Store } from '@ngrx/store';
import { SessionService } from '@service/session/session.service';
import { ProductsService } from '@service/products/products.service';
import { HeaderMenu, buildMenuOptions } from './menu-utils';
import { CacheService } from '@service/cache/cache.service';
import { NotificationPopoverComponent } from '../notification-popover/notification-popover.component';
import { BaseComponent } from '@shared/base/base.component';
import { CookieService } from 'ngx-cookie-service';
import { projectDetailsPageStateKey } from '../../project/store/state';
import { PROJECT_TYPES, IS_WORKFLOW_PROJECT_TYPE } from '@domain/constants/project-types';

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [SearchPipe, CookieService],
	// changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy, AfterViewInit {
	@Input() showLogout: boolean = false;
	@Input() showSignup: boolean = false;
	@Input() showSignin: boolean = false;
	user;
	URLS = URLS;
	ACCOUNT_TYPE = ACCOUNT_TYPE;
	isAdminOrOwner: boolean;
	isFullWidthLayout = false;

	IS_CARRIERS_ENABLED: boolean = false;
	FREE_TRIAL_DAYS_REMAINING = null;

	isCarrierFullProfileOpened: boolean = false;
	carrierLaneData: any = null;
	contactList: ContactsTable[] = null;
	recentFeedbackList: Array<CarrierFeedback> = null;
	@ViewChild('laneDetails') laneDetails: LaneDetailsComponent;
	@ViewChild('quickSearchComponent') quickSearchComponent: QuickSearchComponent;
	@ViewChild('profilePopover') profilePopoverComponent: ProfilePopoverComponent;
	@ViewChild('notificationPopover', { static: false })
	notificationsPopoverComponent: NotificationPopoverComponent;
	S3_CSV_TEMPLATE = S3_CSV_TEMPLATE;

	USER_ACCOUNT_TYPE: string = null;
	showCapacitySearchNotification: boolean = false;
	isUploadingFile: boolean = false;

	environment = environment;
	highlightLandingPageSection: boolean = false;
	private openProfilePanelSubscription: Subscription;
	private openNotificationPopoverWithPanelExpandedSubscription: Subscription;

	TEMPLATE_TYPE = TEMPLATE_TYPE;

	MENU_OPTIONS!: HeaderMenu;

	// Feature flag getters for conditional navigation
	get showSplitProjectsMenu(): boolean {
		return environment.FEATURE_FLAGS.SPLIT_PROJECTS_MENU_ENABLED;
	}

	get showOriginalProjectsMenu(): boolean {
		return !environment.FEATURE_FLAGS.SPLIT_PROJECTS_MENU_ENABLED;
	}

	// CN-8861 — the account can reach the Bulk Rating / RFP screen: same entitlement
	// guards as the legacy Automation Tools "RFPs" link.
	get bulkRatingEntitled(): boolean {
		return this.showMenuItem('lanesRFP') && !this.utilService.isAccountTypeCarrier();
	}

	// CN-8861 — Procurement becomes a [Projects, Bulk Rating] dropdown ONLY where the
	// split-projects nav exists AND the account can actually reach Bulk Rating. Everywhere
	// else Procurement stays a plain link and Bulk Rating remains under Automation Tools
	// (relabeled), so enabling the flag never orphans the screen.
	get showProcurementDropdown(): boolean {
		return (
			this.showSplitProjectsMenu &&
			!!this.MENU_OPTIONS?.PROJECTS &&
			this.accountSettings.BULK_RATING_ENABLED &&
			this.bulkRatingEntitled
		);
	}

	// CN-8876 — Carriers have no Procurement nav, so neither the broker Procurement dropdown
	// nor the Automation Tools "RFPs" entry (both `!isAccountTypeCarrier()`-gated) ever surfaces
	// Bulk Rating for them. Give carriers their own top-level Bulk Rating item, gated solely on
	// the deliberate per-account `BULK_RATING_ENABLED` setting (the legacy `lanesRFP` entitlement
	// depends on the `RFP` plan product, which carriers may have present-but-disabled). This is
	// a separate, carrier-only path — the broker gating above is intentionally left untouched.
	get showCarrierBulkRating(): boolean {
		return this.utilService.isAccountTypeCarrier() && this.accountSettings.BULK_RATING_ENABLED;
	}

	// variable for conditions
	isNotAdmin: boolean = false;
	activeMenu = {
		home: false,
		projects: false,
		procurement: false,
		workflows: false,
		capacity: false,
		pricing: false,
		profile: false,
		network: false,
		customers: false,
		scorecards: false,
		loads: false,
		offers: false,
		// carriers menu
		carrierDashboard: false,
		carrierLanes: false,
		carrierAvailableLoads: false,
		carrierBookedLoads: false,
		carrierOffers: false,
		carrierQualityScore: false,
		carriersNetwork: false,
		carrierDiscover: false,
		carrierProfile: false,
		carrierBulkRating: false,
	};
	private readonly ROUTE_MENU_MAP = {
		home: ['/#/dashboard'],
		projects: ['/#/projects', '/#/v2/projects'],
		procurement: ['/#/v2/procurement', '/#/projects/project_details', '/#/users/automation/rfps'],
		workflows: ['/#/v2/workflows', '/#/projects/project_details'],
		capacity: ['capacity'],
		carriersNetwork: ['carriers/network'],
		pricing: ['pricing'],
		profile: [''],
		network: ['/#/carriers', '/#/v2/carriers'],
		customers: ['users/customers'],
		scorecards: [
			'reports/scorecards', 'reports/offer-analytics', 'reports/lane-analytics',
			'reports/lane-carrier-analytics', 'reports/workflow-performance',
			// Legacy paths (redirects) — keep matching during transition
			'users/scorecards', 'v2/offer-analytics', 'v2/lane-analytics', 'v2/lane-carrier-stats'
		],
		loads: ['v2/loadsList', 'v2/loads/', 'quotedetails', 'load_details'],
		offers: ['v2/offers'],
		// carrier routes
		carrierDashboard: ['carriers/dashboard'],
		carrierLanes: ['carriers/lanes'],
		carrierAvailableLoads: ['carriers/availableLoads'],
		carrierBookedLoads: ['carriers/bookedLoads'],
		carrierOffers: ['carriers/offers'],
		carrierQualityScore: ['carriers/qualityScore'],
		carrierDiscover: ['carriers/discover'],
		carrierProfile: ['carriers/profile'],
		carrierBulkRating: ['users/automation/rfps'],
	};
	isAccountTypeCarrier: boolean = false;

	smallDeviceScreen: boolean = false;

	constructor(
		private router: Router,
		private restService: RestService,
		private urlService: UrlService,
		public cargochief: Cargochief,
		public utilService: UtilService,
		public sharedService: SharedService,
		private indexedDB: IndexedDBService,
		private segmentWrapper: SegmentWrapperService,
		public intercomService: IntercomService,
		public accountSettings: AccountSettingsService,
		private activatedRoute: ActivatedRoute,
		private headerService: HeaderService,
		public projectHelperService: ProjectHelperService,
		private store: Store,
		public productsService: ProductsService,
		private cacheService: CacheService,
		private cookieService: CookieService,
		private sessionService: SessionService,
		media: MediaObserver
	) {
		super();

		media
			.asObservable()
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe((change) => {
				this.smallDeviceScreen =
					change.filter((item) => item.mqAlias == 'xs' || item.mqAlias == 'sm')?.length > 0;
			});
	}

	ngOnInit() {
		this.setIsNotAdmin();
		if (this.cargochief.CARRIER?.id) {
			this.ROUTE_MENU_MAP.profile = [`carriers/${this.cargochief.CARRIER.id}`];
		}
		this.user = this.cargochief.getLoginSetupUser();
		this.USER_ACCOUNT_TYPE = this.cargochief.USER_ACCOUNT_TYPE;
		this.isAdminOrOwner = this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner();
		const planName: string = this.utilService.getUserPlanName();
		// this.initializeSidenavLayout();
		if (
			this.utilService.isAccountTypeBroker() ||
			this.utilService.isAccountTypeShipper() ||
			(IS_NOT_NULL_OR_UNDEFINED(planName) &&
				planName.toLowerCase() == PLAN_NAMES.PRICING.toLowerCase()) ||
			this.utilService.isAccountTypeCarrier()
		) {
			this.isFullWidthLayout = true;
		}
		this.layoutSwitched();
		this.IS_CARRIERS_ENABLED = this.isCarriersEnabled();

		this.sharedService
			.getfeedbackNotificationEvent()
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe((data: any) => {
				if (IS_NOT_NULL_OR_UNDEFINED(data)) {
					this.showCapacitySearchNotification = data;
				}
			});
		this.activatedRoute.queryParams
			.pipe(takeUntil(this.isDestroyed$), distinctUntilChanged())
			.subscribe((params) => {
				if (IS_NULL_OR_UNDEFINED(params.capacitySearch)) {
					this.showCapacitySearchNotification = false;
				}
			});
		if (IS_NULL_OR_UNDEFINED(this.openProfilePanelSubscription)) {
			this.openProfilePanelSubscription = this.headerService
				.getOpenProfilePanelEvent()
				.pipe(take(1), takeUntil(this.isDestroyed$))
				.subscribe(() => {
					this.openProfilePanel();
				});
		}
		if (IS_NULL_OR_UNDEFINED(this.openNotificationPopoverWithPanelExpandedSubscription)) {
			this.openNotificationPopoverWithPanelExpandedSubscription = this.headerService
				.getOpenNotificationsPanelEvent()
				.pipe(takeUntil(this.isDestroyed$), distinctUntilChanged())
				.subscribe(({ panelToOpen }) => {
					this.openNotificationPopover({ panelToOpen });
				});
		}

		// set menu visible
		this.MENU_OPTIONS = buildMenuOptions(
			this.isPaymentView(),
			this.showLogout,
			this.isFullWidthLayout,
			this.IS_CARRIERS_ENABLED,
			this.utilService,
			this.productsService,
			this.accountSettings,
			this.cargochief
		);

		this.setActiveMenu();
		this.router.events
			.pipe(
				takeUntil(this.isDestroyed$),
				distinctUntilChanged(),
				filter((event) => event instanceof NavigationEnd)
			)
			.subscribe((event) => {
				this.setActiveMenu();
			});

		// Listen to project details state changes to update menu highlighting
		this.store
			.select((state) => state[projectDetailsPageStateKey])
			.pipe(
				takeUntil(this.isDestroyed$),
				filter((projectState) => projectState?.projectDTO != null)
			)
			.subscribe(() => {
				const isProjectDetailsPage =
					window.location.href.indexOf('/#/projects/project_details') > 0;
				if (isProjectDetailsPage && this.showSplitProjectsMenu) {
					this.setActiveMenu();
				}
			});

		this.isAccountTypeCarrier = this.utilService.isAccountTypeCarrier();
	}

	ngAfterViewInit() {
		this.intercomService.bootAndShow();
	}

	isUserLoggedIn() {
		return !this.utilService.isNullOrUndefined(this.cargochief.getUser());
	}

	logout() {
		const url = this.urlService.logoutUrl();
		this.restService
			.POST(url, {})
			.pipe(
				takeUntil(this.isDestroyed$),
				finalize(() => {
					this.endSession();
					this.cookieService.deleteAll();
					this.shutdownIntercom();
				})
			)
			.subscribe(
				(result) => {},
				(error) => {}
			);
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.logged_out, {});
	}

	endSession() {
		this.unsubscribeAllObservables();
		this.sessionService.endSession();
	}

	isDemoAccount() {
		return this.accountSettings.DEMO_ACCOUNT;
	}

	layoutSwitched() {
		this.cargochief.setItem({ [CARGOCHIEF_KEYS.LAYOUT_SETTING]: this.isFullWidthLayout });
		this.sharedService.sendLayoutToggled(this.isFullWidthLayout);
		if (!this.utilService.isNullOrUndefined(this.indexedDB.application.indexedDB.db)) {
			this.indexedDB.updateLayoutSetting(this.isFullWidthLayout);
		}
	}

	initializeSidenavLayout() {
		if (this.utilService.isAccountTypeBroker() || this.utilService.isAccountTypeShipper()) {
			if (!this.utilService.isNullOrUndefined(this.cargochief.getLayoutSetting())) {
				this.isFullWidthLayout =
					this.cargochief.getLayoutSetting() == 'true'
						? JSON.parse(this.cargochief.getLayoutSetting())
						: false;
				this.layoutSwitched();
			}
			if (!this.utilService.isNullOrUndefined(this.indexedDB.application.indexedDB.db)) {
				this.indexedDB
					.getAll(INDEXED_DB.LAYOUT)
					.pipe(takeUntil(this.isDestroyed$))
					.subscribe((items: any) => {
						if (
							!this.utilService.isNullOrUndefined(items) &&
							!this.utilService.isNullOrUndefined(items[0])
						) {
							this.isFullWidthLayout = items[0];
							this.layoutSwitched();
						}
					});
			}
		}
	}

	showSettings(): boolean {
		if (!this.utilService.isNullOrUndefined(this.cargochief.getAccount())) {
			return this.productsService.CARRIER_RECRUITING;
		} else {
			return false;
		}
	}

	isProjectsEnabled() {
		return (
			this.productsService.PROJECT_CARRIERS &&
			this.showLogout &&
			this.isFullWidthLayout &&
			this.isNotAdmin
		);
	}

	isPaymentView(): boolean {
		return window.location.href.indexOf('/#/register/complete_payment') > 0;
	}

	isCarriersEnabled(): boolean {
		return (
			this.showLogout &&
			this.isFullWidthLayout &&
			this.isNotAdmin &&
			this.productsService.CARRIER_PROFILES &&
			!this.isPaymentView()
		);
	}

	setIsNotAdmin() {
		this.isNotAdmin =
			this.utilService.isAccountTypeBroker() ||
			this.utilService.isAccountTypeShipper() ||
			this.utilService.isAccountTypeCarrier();
	}

	isFreeTrial(): boolean {
		const account = this.cargochief.getAccount();
		if (
			!this.utilService.isNullOrUndefined(account) &&
			!this.utilService.isNullOrUndefined(account.trialEnd) &&
			!this.utilService.isNullOrUndefined(account.cancelAtPeriodEnd) &&
			!account.cancelAtPeriodEnd
		) {
			this.FREE_TRIAL_DAYS_REMAINING = DIFFERENCE_IN_DAYS(
				new Date(),
				new Date(account.trialEnd * 1000)
			);
			if (this.FREE_TRIAL_DAYS_REMAINING <= 14 && this.FREE_TRIAL_DAYS_REMAINING >= 0) {
				return true;
			}
		}
		return false;
	}

	isManageUsersEnabled() {
		return this.productsService.MULTIPLE_USERS;
	}

	shutdownIntercom() {
		this.intercomService.shutdown();
	}

	openCarrierFullProfile(carrier: CarrierTable) {
		this.cargochief.setItem({ [CARGOCHIEF_KEYS.IS_FULL_PROFILE_OPENED]: true });
		this.carrierLaneData = null;
		this.contactList = null;
		this.recentFeedbackList = null;
		this.isCarrierFullProfileOpened = true;
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_viewed_full_profile, carrier);
		this.getCarrierContacts(carrier);
		this.getRecentFeedbackList(carrier.id);
	}

	getCarrierContacts(carrier: CarrierTable) {
		const contactsUrl = this.urlService.contactsUrl(carrier.id);
		this.restService
			.GET(contactsUrl)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.contact_requested, result);
					this.contactList = result['data'];
					this.carrierLaneData = {
						lane: {
							businessName: carrier.businessName,
							customerCarrier: carrier.customerCarrier,
							firmMc: carrier.mc,
							firmDot: carrier.dot,
							powerUnits: carrier.powerUnits,
							drivers: carrier.drivers,
							carrierId: carrier.id,
							carrierHQ: carrier.carrierHQ,
							active: carrier.active,
							vettingSource: carrier.vettingSource,
							carrierOnboardedOn: carrier.carrierOnboardedOn,
							ccBookingRateDisplay: carrier.ccBookingRateDisplay,
							ccEngagementRateDisplay: carrier.ccEngagementRateDisplay,
							ccFalloffRateDisplay: carrier.ccFalloffRateDisplay,
							ccTotalBookedLoads: carrier.ccTotalBookedLoads,
							ccScore: carrier.ccScore,
							vettingOverallDisplay: carrier?.vettingOverallDisplay,
							vettingAuthorityDisplay: carrier?.vettingAuthorityDisplay,
							vettingInsuranceDisplay: carrier?.vettingInsuranceDisplay,
							vettingOperationsDisplay: carrier?.vettingOperationsDisplay,
							vettingSafetyDisplay: carrier?.vettingSafetyDisplay,
							vettingOtherDisplay: carrier?.vettingOtherDisplay,
							infractionDetails: carrier?.infractionDetails,
							lastVettingTime: carrier?.lastVettingTime,
							phone: carrier.phone,
							cellPhone: carrier.cellPhone,
							businessEmailAddress: carrier.businessEmailAddress,
							representative1: carrier.representative1,
							trailers: carrier.trailers,
						},
						contactList: this.contactList,
					};
				},
				(error) => {
					this.utilService.showSnackbar(
						'Cannot fetch contact list.',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.contactList = [];
				}
			);
	}

	getRecentFeedbackList(id: string) {
		const url = this.urlService.getCarrierFeedback(id, {
			pageSize: CARRIER_FEEDBACK_SETTINGS.PAGE_SIZE,
		});
		this.restService
			.GET(url)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe((response) => {
				this.recentFeedbackList = response['data'];
			});
	}

	closeLaneDetails(laneDetails: LaneDetailsComponent) {
		if (!this.utilService.isNullOrUndefined(laneDetails)) {
			this.cargochief.removeItem(CARGOCHIEF_KEYS.IS_FULL_PROFILE_OPENED);
			this.laneDetails = Object.assign({}, laneDetails);
			this.sharedService.sendCarrierSearchEvent({ type: 'note', value: this.laneDetails });
			this.isCarrierFullProfileOpened = false;
		}
	}

	@HostListener('document:keydown.escape', ['$event']) onKeydownHandler(event: KeyboardEvent) {
		event.stopPropagation();
		if (this.isCarrierFullProfileOpened) {
			event.stopImmediatePropagation();
		}
		this.closeCarrierFullProfile();
	}

	closeCarrierFullProfile() {
		if (!this.utilService.isNullOrUndefined(this.laneDetails)) {
			this.closeLaneDetails(this.laneDetails);
		}
	}

	fireViewedProjectsListEvent(type: string = 'projects') {
		const payload = {
			user: this.utilService.getUserDataForSegmentEvent(),
			type: type,
		};
		this.segmentWrapper.fireSegmentEvent(SegmentEventsConstants.user_viewed_project_list, payload);
	}

	capacityNotificationClicked() {
		this.sharedService.sendfeedbackNotificationClickedEvent(true);
	}

	showMenuItem(item: string) {
		switch (item) {
			case 'bulkTools':
				return (
					this.productsService.BULK_MATCHING ||
					(environment.PRICE_LANES_BUTTON_ENABLED && this.productsService.PRICING)
				);
			case 'lanesRFP':
				return environment.PRICE_LANES_BUTTON_ENABLED && this.productsService.RFP;
			case 'bulkMatch':
				return this.productsService.BULK_MATCHING;
			case 'team':
				return (
					(this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner()) &&
					this.utilService.isPaidUser() &&
					!this.utilService.isAccountTypeCarrier()
				);
			case 'emailTemplates':
				return environment.EMAIL_TEMPLATES && this.accountSettings.CUSTOIZABLE_EMAILS;
			case 'carrierAutomationPage':
				return this.productsService.CARRIER_PROFILES && !this.utilService.isAccountTypeCarrier();
			case 'carrierManageUsers':
				return this.utilService.isAccountTypeCarrierWithAdminRoleAndConfirmedEmail();
			default:
				return false;
		}
	}

	integrationsEnabled() {
		return (
			this.productsService.INTEGRATIONS &&
			environment.INTEGRATIONS &&
			(this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner())
		);
	}

	isAuthorizedForCustomerScorecard(): boolean {
		return (
			!this.utilService.isAccountTypeShipper() &&
			this.accountSettings.ROLE_BASED_CUSTOMER_DATA_ACCESS &&
			(this.utilService.isUserRoleAdmin() ||
				!this.accountSettings.HIDE_CUSTOMER_DETAILS_FROM_NON_ADMIN_USERS)
		);
	}

	@HostListener('document:keydown./', ['$event'])
	handleKeyboardEvent(event: KeyboardEvent) {
		const htmlElement = event.target as HTMLInputElement;
		if (
			!this.quickSearchComponent.isQuickSearchClicked &&
			htmlElement &&
			htmlElement.getAttribute('data-placeholder') != 'URL' &&
			htmlElement.tagName !== 'INPUT'
		) {
			this.quickSearchComponent.animate();
		}
	}

	openProfilePanel() {
		this.highlightLandingPageSection = true;
		this.profilePopoverComponent.openProfilePopover();
		setTimeout(() => {
			this.highlightLandingPageSection = false;
		}, 5000);
	}

	openNotificationPopover({ panelToOpen }) {
		this.notificationsPopoverComponent?.openNotificationPopoverWithPanelExpanded({ panelToOpen });
	}

	unsubscribeAllObservables() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.openProfilePanelSubscription)) {
			this.openProfilePanelSubscription.unsubscribe();
		}
		if (IS_NOT_NULL_OR_UNDEFINED(this.openNotificationPopoverWithPanelExpandedSubscription)) {
			this.openNotificationPopoverWithPanelExpandedSubscription.unsubscribe();
		}
	}

	isEmailRoutingEnabled(): boolean {
		return (
			this.accountSettings.EMAIL_ROUTING &&
			(this.utilService.isUserRoleAdmin() || this.utilService.isUserRoleOwner())
		);
	}

	setActiveMenu(): void {
		const isProjectDetailsPage = window.location.href.indexOf('/#/projects/project_details') > 0;

		if (isProjectDetailsPage && this.showSplitProjectsMenu) {
			// For project details page, check the project type to determine which menu to highlight
			this.store
				.select((state) => state[projectDetailsPageStateKey])
				.pipe(take(1))
				.subscribe((projectState) => {
					const projectType = projectState?.projectDTO?.type;

					// Reset all project-related menu items
					this.activeMenu.projects = false;
					this.activeMenu.procurement = false;
					this.activeMenu.workflows = false;

					// Set the active menu based on project type
					if (projectType === PROJECT_TYPES.PROCUREMENT.value) {
						this.activeMenu.procurement = true;
					} else if (IS_WORKFLOW_PROJECT_TYPE(projectType)) {
						this.activeMenu.workflows = true;
					} else if (projectType) {
						// If project type exists but doesn't match, default to workflows
						this.activeMenu.workflows = true;
					}

					// Set other menu items normally
					Object.entries(this.ROUTE_MENU_MAP).forEach(([key, routes]) => {
						if (key !== 'projects' && key !== 'procurement' && key !== 'workflows') {
							this.activeMenu[key] = this.isActiveClass(routes);
						}
					});
				});
		} else {
			// Normal behavior for other pages
			Object.entries(this.ROUTE_MENU_MAP).forEach(([key, routes]) => {
				this.activeMenu[key] = this.isActiveClass(routes);
			});
		}
	}

	isActiveClass(route: Array<string>): boolean {
		let isMenuActive = false;
		route.map((path) => {
			if (window.location.href.indexOf(path) > 0) {
				isMenuActive = true;
			}
		});
		return isMenuActive;
	}

	ngOnDestroy() {
		super.ngOnDestroy();
		this.unsubscribeAllObservables();
	}
}
